import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { SKIP_ERROR_HANDLER } from '../../../core/interceptors/error.interceptor';
import { DashboardResponse, RachaResponse, CargaTrabajoResponse, ResumenDetalladoResponse, EscuadronResponse } from '../models/dashboard.model';
import { InsigniaResponse } from '../models/insignia.model';
import { NotificacionResponse, PageNotificacionResponse, MarcarLeidasRequest, MarcarLeidasResponse } from '../models/notificacion.model';
import { RegistroTiempoRequest, RegistroTiempoResponse, RegistroTiempoGroupResponse, CategoriaImprevistoResponse, TareaOrganizacionalResponse, AnimarEquipoRequest } from '../models/registro-tiempo.model';
import { EquipoResponse, EquipoRequest, MiembroResponse, MiembroRequest } from '../models/equipo.model';
import { RachaExcepcionRequest, RachaExcepcionResponse } from '../models/racha-excepcion.model';
import { WeekRange } from '../models/ui.model';

@Injectable({
  providedIn: 'root'
})
export class SelfSpaceApiService {
  private readonly baseUrl: string;
  private readonly registroUrl: string;
  private readonly adminUrl: string;

  constructor(private readonly http: HttpClient) {
    this.baseUrl = environment.backEndHost + environment.timeTrackerEndPoint + '/self-space';
    this.registroUrl = environment.backEndHost + environment.timeTrackerEndPoint + '/registro-tiempos';
    this.adminUrl = environment.backEndHost + environment.timeTrackerEndPoint + '/admin/self-space';
  }

  /** Convert 'YYYY-MM-DD' to 'YYYY-MM-DDT00:00:00Z' for backend Instant params */
  private toInstant(dateStr: string): string {
    return dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00Z`;
  }

  /** HttpContext that suppresses the global error dialog */
  private get silentContext(): HttpContext {
    return new HttpContext().set(SKIP_ERROR_HANDLER, true);
  }

  // === Dashboard Aggregated ===

  getDashboard(email: string, weekRange: WeekRange): Observable<DashboardResponse> {
    const params = new HttpParams()
      .set('email', email)
      .set('startDate', this.toInstant(weekRange.startDate))
      .set('endDate', this.toInstant(weekRange.endDate));
    return this.http.get<DashboardResponse>(`${this.baseUrl}/dashboard`, { params });
  }

  // === Individual Endpoints (for partial refresh) ===

  getRacha(email: string, weekRange: WeekRange, silent = false): Observable<RachaResponse> {
    const params = new HttpParams()
      .set('email', email)
      .set('startDate', this.toInstant(weekRange.startDate))
      .set('endDate', this.toInstant(weekRange.endDate));
    return this.http.get<RachaResponse>(`${this.baseUrl}/racha`, { params, ...(silent && { context: this.silentContext }) });
  }

  getCargaTrabajo(email: string, weekRange: WeekRange, silent = false): Observable<CargaTrabajoResponse> {
    const params = new HttpParams()
      .set('email', email)
      .set('startDate', this.toInstant(weekRange.startDate))
      .set('endDate', this.toInstant(weekRange.endDate));
    return this.http.get<CargaTrabajoResponse>(`${this.baseUrl}/carga-trabajo`, { params, ...(silent && { context: this.silentContext }) });
  }

  getResumenDetallado(email: string, weekRange: WeekRange, silent = false): Observable<ResumenDetalladoResponse> {
    const params = new HttpParams()
      .set('email', email)
      .set('startDate', this.toInstant(weekRange.startDate))
      .set('endDate', this.toInstant(weekRange.endDate));
    return this.http.get<ResumenDetalladoResponse>(`${this.baseUrl}/resumen-detallado`, { params, ...(silent && { context: this.silentContext }) });
  }

  getEscuadron(email: string, weekRange: WeekRange): Observable<EscuadronResponse> {
    const params = new HttpParams()
      .set('email', email)
      .set('startDate', this.toInstant(weekRange.startDate))
      .set('endDate', this.toInstant(weekRange.endDate));
    return this.http.get<EscuadronResponse>(`${this.baseUrl}/escuadron`, { params });
  }

  getInsignias(email: string): Observable<InsigniaResponse[]> {
    const params = new HttpParams().set('email', email);
    return this.http.get<InsigniaResponse[]>(`${this.baseUrl}/insignias`, { params });
  }

  // === Notificaciones ===

  getNotificaciones(email: string, page: number = 0, size: number = 20): Observable<NotificacionResponse[]> {
    const params = new HttpParams()
      .set('email', email)
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<PageNotificacionResponse>(`${this.baseUrl}/notificaciones`, { params })
      .pipe(map(response => response.content || []));
  }

  marcarNotificacionesLeidas(request: MarcarLeidasRequest): Observable<MarcarLeidasResponse> {
    return this.http.put<MarcarLeidasResponse>(`${this.baseUrl}/notificaciones/marcar-leidas`, request);
  }

  notificarBloqueo(
    email: string,
    request: { hitoProyectoId?: number | null; mensaje?: string; proyectoId?: number | null }
  ): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/notificar-bloqueo`, { ...request, email });
  }

  // === Escuadrón ===

  animarEscuadron(request: AnimarEquipoRequest, weekRange: WeekRange): Observable<void> {
    const params = new HttpParams()
      .set('startDate', this.toInstant(weekRange.startDate))
      .set('endDate', this.toInstant(weekRange.endDate));
    return this.http.post<void>(`${this.baseUrl}/escuadron/animar`, request, { params });
  }

  // === Categorías de Imprevisto ===

  getCategoriasImprevisto(activa?: boolean): Observable<CategoriaImprevistoResponse[]> {
    let params = new HttpParams();
    if (activa !== undefined) {
      params = params.set('activa', activa.toString());
    }
    return this.http.get<CategoriaImprevistoResponse[]>(`${this.baseUrl}/categorias-imprevisto`, { params });
  }

  crearCategoriaImprevisto(request: Partial<CategoriaImprevistoResponse>): Observable<CategoriaImprevistoResponse> {
    return this.http.post<CategoriaImprevistoResponse>(`${this.baseUrl}/categorias-imprevisto`, request);
  }

  actualizarCategoriaImprevisto(id: number, request: Partial<CategoriaImprevistoResponse>): Observable<CategoriaImprevistoResponse> {
    return this.http.put<CategoriaImprevistoResponse>(`${this.baseUrl}/categorias-imprevisto/${id}`, request);
  }

  eliminarCategoriaImprevisto(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/categorias-imprevisto/${id}`);
  }

  // === Tareas Organizacionales ===

  getTareasOrganizacionales(activa?: boolean, categoriaImprevistoId?: number): Observable<TareaOrganizacionalResponse[]> {
    let params = new HttpParams();
    if (activa !== undefined) {
      params = params.set('activa', activa.toString());
    }
    if (categoriaImprevistoId !== undefined) {
      params = params.set('categoriaImprevistoId', categoriaImprevistoId.toString());
    }
    return this.http.get<TareaOrganizacionalResponse[]>(`${this.baseUrl}/tareas-organizacionales`, { params });
  }

  crearTareaOrganizacional(request: Partial<TareaOrganizacionalResponse>): Observable<TareaOrganizacionalResponse> {
    return this.http.post<TareaOrganizacionalResponse>(`${this.baseUrl}/tareas-organizacionales`, request);
  }

  // === Registro de Tiempo (existing endpoints) ===

  crearRegistro(registro: RegistroTiempoRequest): Observable<RegistroTiempoResponse> {
    return this.http.post<RegistroTiempoResponse>(this.registroUrl, registro, { context: this.silentContext });
  }

  actualizarRegistro(id: number, registro: RegistroTiempoRequest): Observable<RegistroTiempoResponse> {
    return this.http.put<RegistroTiempoResponse>(`${this.registroUrl}/${id}`, registro, { context: this.silentContext });
  }

  eliminarRegistro(id: number): Observable<void> {
    return this.http.delete<void>(`${this.registroUrl}/${id}`);
  }

  getRegistrosGroupedByEmail(email: string, startDate?: string, endDate?: string, silent = false, page = 0, size = 100): Observable<RegistroTiempoGroupResponse[]> {
    let params = new HttpParams()
      .set('email', email)
      .set('page', page.toString())
      .set('size', size.toString());
    if (startDate) {
      params = params.set('startDate', this.toInstant(startDate));
    }
    if (endDate) {
      params = params.set('endDate', this.toInstant(endDate));
    }
    return this.http.get<any>(`${this.registroUrl}/grouped-by-email`, { params, ...(silent && { context: this.silentContext }) })
      .pipe(map(response => {
        // Handle both paginated ({ content: [...] }) and raw array responses
        if (Array.isArray(response)) return response;
        if (response?.content && Array.isArray(response.content)) return response.content;
        return [];
      }));
  }

  // === Equipos (Admin) ===

  getEquipos(activo?: boolean): Observable<EquipoResponse[]> {
    let params = new HttpParams();
    if (activo !== undefined) {
      params = params.set('activo', activo.toString());
    }
    return this.http.get<EquipoResponse[]>(`${this.adminUrl}/equipos`, { params });
  }

  getEquipo(id: number): Observable<EquipoResponse> {
    return this.http.get<EquipoResponse>(`${this.adminUrl}/equipos/${id}`);
  }

  crearEquipo(request: EquipoRequest): Observable<EquipoResponse> {
    return this.http.post<EquipoResponse>(`${this.adminUrl}/equipos`, request);
  }

  actualizarEquipo(id: number, request: EquipoRequest): Observable<EquipoResponse> {
    return this.http.put<EquipoResponse>(`${this.adminUrl}/equipos/${id}`, { id, ...request });
  }

  eliminarEquipo(id: number): Observable<void> {
    return this.http.delete<void>(`${this.adminUrl}/equipos/${id}`);
  }

  // === Miembros de Equipo (Admin) ===

  getMiembros(equipoId: number, activo?: boolean): Observable<MiembroResponse[]> {
    let params = new HttpParams();
    if (activo !== undefined) {
      params = params.set('activo', activo.toString());
    }
    return this.http.get<MiembroResponse[]>(`${this.adminUrl}/equipos/${equipoId}/miembros`, { params });
  }

  crearMiembro(request: MiembroRequest): Observable<MiembroResponse> {
    return this.http.post<MiembroResponse>(`${this.adminUrl}/miembros`, request);
  }

  eliminarMiembro(id: number): Observable<void> {
    return this.http.delete<void>(`${this.adminUrl}/miembros/${id}`);
  }

  // === Excepciones de Racha (Admin) ===

  getRachaExcepciones(email: string): Observable<RachaExcepcionResponse[]> {
    const params = new HttpParams().set('email', email);
    return this.http.get<RachaExcepcionResponse[]>(`${this.adminUrl}/racha-excepciones`, {
      params,
      context: this.silentContext
    });
  }

  crearRachaExcepcion(request: RachaExcepcionRequest): Observable<RachaExcepcionResponse> {
    return this.http.post<RachaExcepcionResponse>(`${this.adminUrl}/racha-excepciones`, request, {
      context: this.silentContext
    });
  }

  eliminarRachaExcepcion(id: number): Observable<void> {
    return this.http.delete<void>(`${this.adminUrl}/racha-excepciones/${id}`, {
      context: this.silentContext
    });
  }
}
